import {
  type Connection,
  type Publisher,
  type Session as OTSession,
  type Stream,
  type Dimensions,
  type QualityScore,
  type CpuPerformanceState,
  type Subscriber,
} from '@opentok/client';
import { MediaEventType, media } from '../media';
import { CaughtOTError } from '../media/error';
import type {
  OTSignalEvent,
  ConnectionMetadata,
  OTSubscriber,
  OTVideoDisableReason,
} from './types';
import { retry } from '../../shared/errorHandlingUtils/retry';
import { MetricsMonitor, SessionStatsProvider } from './analytics';
import type {
  SessionOptions,
  SessionData,
  UpdatableSessionParticipantData,
  PreferredParticipantVideoQuality,
  SessionParticipant,
  PermanentSessionParticipantData,
  SessionStartParams,
  SessionAddParticipantsParams,
  SessionResumeParams,
  SessionJoinParams,
} from './types';
import { getLogger } from '../../libs/logger';
import {
  ParticipantVideoDisableReason,
  SessionConnectionStatus,
  SessionType,
} from './constants';
import { SignalingEventType } from './constants';
import {
  getParticipantVideoDisableReason,
  parseOTSignalEvent,
} from './helpers';
import { type NetworkQuality } from '@doxyme/ui/components/foundations';
import { type ProcessedNetworkScores } from '../../services/call';
import { SubscriberStats, PublisherStats } from './stats';
import { statsCollectors, StatsCollectors, StatsToLog } from './metrics';
import { Observable, Subject } from 'rxjs';
import {
  ActiveSpeakerId,
  AudioLevelsMap,
  observeActiveSpeaker,
  observeAudioLevels,
  type AudioLevelChanges,
} from './audioLevels';
import {
  type ResponseWithError,
  type SessionResponseData,
  ensureSuccessfulSessionApiResponse,
} from '../../services/sessionApiError';
import { sessionApiService } from '../../services';
import { WebRtcSessionTypeMap } from '../../services/sessionApi.helper';
import {
  SessionSetupStep,
  sessionSetupTimer,
} from '../../hooks/useCallState/states/callInitTimer';
import { getUsername } from '../../features/realtime/realtime.selectors';
import { WebRtcSessionV2 } from '../../services/graphQlTypes.generated';
import { CustomError } from '@doxyme/api-services';
import { CustomEmitter } from '../../webrtc/utils/emitter';
import { getFrontendConfigLoader } from '@doxyme/config';

// Events
export enum SessionEventType {
  ParticipantJoined = 'participantJoined',
  ParticipantUpdated = 'participantUpdated',
  ParticipantVideoDisabled = 'ParticipantVideoDisabled',
  ParticipantVideoDisableWarning = 'participantVideoDisableWarning',
  ParticipantLeft = 'participantLeft',
  SessionDisconnected = 'sessionDisconnected',
  ConnectionStatusChanged = 'connectionStatusChanged',
  /**
   * Triggers when a caption is received.
   */
  CaptionReceived = 'captionReceived',

  /**
   * Triggers when captions failed to start or stopped working.
   */
  CaptionFailed = 'captionFailed',
}

export type SessionEventData = {
  participantId?: string;
  speakerId?: string;
  connectionStatus?: SessionConnectionStatus;
  videoDisabledReason?: ParticipantVideoDisableReason;
  videoDisableWarning?: boolean;
  username?: string;
  identityId?: string;
  caption?: string;
  isFinal?: boolean;
};

export class Session
  extends CustomEmitter<SessionEventType, SessionEventData>
  implements SessionStatsProvider
{
  private opentokClient?: Promise<typeof OT>;

  private readonly logger = getLogger('SessionVonage');

  private readonly subscribers: Map<string, OTSubscriber> = new Map();

  private readonly subscriberStats: WeakMap<OTSubscriber, SubscriberStats> =
    new WeakMap();

  private publisherStats?: PublisherStats;

  private readonly screenSubscribers: Map<string, OTSubscriber> = new Map();

  private readonly participantsPreferredQualityMap: Map<
    string,
    PreferredParticipantVideoQuality
  > = new Map();

  private currentSession?: OTSession;

  private ownIdentityId?: string;

  private username?: string;

  private lastCPUPerformanceState?: CpuPerformanceState;

  private lastQualityScore: Record<string, QualityScore> = {};

  private lastNetworkScores?: ProcessedNetworkScores;

  private stopRepublishingOnSourceUpdate = (): void => undefined;

  /**
   * Keep track of participants with captions enabled. We will be using it as s single source of truth for deciding
   * whether to start / stop subscribing to captions or start / stop publishing them.
   */
  private participantsWithCaptionsEnabledMap = new Map<string, boolean>();

  /**
   * Only way to receive your own captions is to create a hidden subscriber (to the local publisher's stream).
   * @link https://developer.vonage.com/en/video/guides/live-caption?lang=javascript#:~:text=is%20my%20sentence%22-,Receiving%20your%20own%20live%20captions,-The%20Vonage%20web
   */
  private selfCaptionsSubscriber: OT.Subscriber | undefined;

  private participants = new Map<string, SessionParticipant>();

  private readonly participantsTempData = new Map<
    string,
    Partial<SessionParticipant>
  >();

  private readonly participantsPermanentData = new Map<
    string,
    PermanentSessionParticipantData
  >();

  private _sessionData?: SessionData;

  private _connectionStatus: SessionConnectionStatus | undefined;

  private _sessionOptions?: SessionOptions;

  private metricsMonitor?: MetricsMonitor;

  /**
   * To be set when captions enabled for the session. To be used by the provider to stop the captions in stopCaptions callback.
   */
  private captionsId?: string;

  private readonly audioLevelChanges: Subject<AudioLevelChanges>;

  readonly audioLevels: Observable<AudioLevelsMap>;

  readonly activeSpeaker: Observable<ActiveSpeakerId>;

  /**
   * Ensures there's only ever one concurrent session starting at any given time.
   * You may ask why but that remains a mystery.
   */
  private sessionStartGuard: Promise<void> | undefined = undefined;

  constructor(private vonageApiKey: string) {
    super();

    if (typeof window !== 'undefined') {
      // SSR guard
      this.opentokClient = import('@opentok/client');
    }

    this.audioLevelChanges = new Subject<AudioLevelChanges>();

    this.audioLevels =
      typeof window !== 'undefined'
        ? observeAudioLevels(this.audioLevelChanges)
        : new Observable<AudioLevelsMap>();

    this.activeSpeaker =
      typeof window !== 'undefined'
        ? observeActiveSpeaker(this.audioLevels)
        : new Observable<ActiveSpeakerId>();

    this.metricsMonitor = new MetricsMonitor(this, this.getMetricAggregators());
  }

  async changeRemoteAudioOutputDevice(deviceId: string): Promise<void> {
    const allSubscribers = [
      ...this.subscribers.values(),
      ...this.screenSubscribers.values(),
    ];
    try {
      for await (const subscriber of allSubscribers) {
        await this.setAudioOutput(subscriber, deviceId);
      }
    } catch (error) {
      this.logger.error('[SessionVonage/changeRemoteAudioOutputDevice]', error);
      throw error;
    }
  }

  private async setAudioOutput(
    subscriber: OTSubscriber,
    deviceId: string | undefined,
  ): Promise<void> {
    if (!deviceId) {
      return;
    }
    try {
      await subscriber.videoElement()?.setSinkId?.(deviceId);
    } catch (error) {
      this.logger.warn('failed to set sink id', {
        deviceId,
        error,
      });
    }
  }

  private async startSession(): Promise<void> {
    if (!this._sessionData) {
      this.logger.warn('[SessionVonage/startSession] missing data');

      throw new CustomError(`[Session/startSession] _sessionData is undefined`);
    }

    if (!this._sessionOptions) {
      throw new CustomError(
        `[Session/startSession] _sessionOptions is undefined`,
      );
    }

    const opentok = await this.opentokClient!;

    this.ownIdentityId = this._sessionData.identityId;
    this.username = this._sessionOptions?.username;

    const session = opentok.initSession(
      this.vonageApiKey,
      this._sessionData.sessionId,
    );
    this.currentSession = session;

    const log = (event: string, data = {}): void => {
      this.logger.info(event, {
        sessionId: this._sessionData?.sessionId,
        participantIdentityId: this.ownIdentityId,
        ...data,
      });
    };

    session.on('signal', this.onSignal.bind(this));
    session.on('connectionCreated', this.onConnectionCreated.bind(this));
    session.on('connectionDestroyed', this.onConnectionDestroyed.bind(this));
    session.on('streamCreated', this.onStreamCreated.bind(this));
    session.on('streamPropertyChanged', this.onStreamChanged.bind(this));
    session.on('streamDestroyed', this.onStreamDestroyed.bind(this));
    session.on('sessionConnected', () => {
      log('[SessionVonage/sessionConnected]');

      this.updateConnectionStatus(SessionConnectionStatus.Connected);
    });
    session.on('sessionReconnecting', () => {
      this.logger.warn('[SessionVonage/sessionReconnecting]', {
        sessionId: this._sessionData?.sessionId,
        participantIdentityId: this.ownIdentityId,
      });
      this.updateConnectionStatus(SessionConnectionStatus.Reconnecting);
    });
    session.on('sessionReconnected', () => {
      log('[SessionVonage/sessionReconnected]');

      this.updateConnectionStatus(SessionConnectionStatus.Connected);
    });
    session.on('sessionDisconnected', ({ reason }) => {
      log('[SessionVonage/sessionDisconnected]', { reason });

      this.updateConnectionStatus(SessionConnectionStatus.Disconnected);
    });
    session.on('cpuPerformanceChanged', (event) => {
      this.lastCPUPerformanceState = event.cpuPerformanceState;
    });

    await this._connect(this._sessionData?.sessionToken);
    await this.publish();

    this.stopRepublishingOnSourceUpdate = media.on(
      [MediaEventType.MediaSourceUpdated],
      () => {
        void this.publish();
      },
    );
  }

  /**
   * Connect to the OpenTok session.
   * @param token generated by api-core.
   * @see vonageEngine.service.ts
   * @see https://tokbox.com/developer/sdks/js/reference/Session.html#connect
   * @throws {TypeError} if currentSession is undefined.
   * @throws {CaughtOTError} error from OpenTok SDK.
   */
  private async _connect(token: string): Promise<void> {
    const prefix = '[SessionVonage/connect]';
    const { currentSession: session, logger } = this;

    const attempts = await retry(
      { maxAttempts: 3, delayBetweenAttempts: 500 },
      async (attempt) => {
        logger.debug(prefix, `connecting, attempt ${attempt}`);

        if (!session) {
          throw new TypeError('no session');
        }

        await new Promise<void>((resolve, reject) =>
          session.connect(token, (otError) => {
            if (otError) {
              reject(new CaughtOTError(otError));
            } else {
              resolve();
            }
          }),
        );

        return attempt;
      },
      (error) => {
        logger.error(prefix, error);
      },
    );

    logger.info(prefix, `connected, ${attempts} attempts`);
  }

  /**
   * Start publishing an audio-video stream to the OpenTok session.
   * @see https://tokbox.com/developer/sdks/js/reference/Session.html#publish
   * @throws {CaughtOTError} error from OpenTok SDK.
   * Returns early if currentSession is undefined.
   * Returns early if publisher is undefined.
   */
  private async publish(): Promise<void> {
    const prefix = '[SessionVonage/publish]';
    const {
      currentSession: session,
      publisher,
      audioLevelChanges,
      logger,
    } = this;

    if (!session) {
      logger.error(prefix, 'No session to publish');
      return;
    }

    /**
     * Publishing an undefined Publisher results in publishing the entire media.
     * This condition helps avoid this case
     */
    if (!publisher) {
      logger.warn(
        prefix,
        'Publisher is undefined. Local media not available. Skipped',
      );
      return;
    }
    /**
     * Return if there is no media access
     * When media access is granted a new publisher is created
     * If has media access and no audio or video we still want to publish
     */
    if (!media.hasAudioAccess && !media.hasVideoAccess) {
      logger.warn(
        prefix,
        'Audio and video are unavailable. Nothing to publish. Skipped',
      );
      return;
    }

    const attempts = await retry(
      { maxAttempts: 3, delayBetweenAttempts: 500 },
      async (attempt) => {
        logger.debug(prefix, `publishing, attempt ${attempt}`);
        await new Promise<void>((resolve, reject) =>
          session.publish(publisher, (otError) => {
            if (otError) {
              switch (otError.name) {
                case 'OT_USER_MEDIA_ACCESS_DENIED': {
                  logger.info(prefix, 'accepting OT_USER_MEDIA_ACCESS_DENIED');
                  break;
                }
                case 'OT_CHROME_MICROPHONE_ACQUISITION_ERROR': {
                  /**
                   * If we cannot publish the publisher to the session it should be destroyed to avoid a situation when
                   * we have a publisher that is running locally but it is not published.
                   *
                   * @todo Currently it is applied only for this kind of error as it is not recoverable but in the future we would need to stop local media if publishing failed on the third attempt no matter what error is.
                   * @description More information here https://doxyme.atlassian.net/browse/PROD-1171
                   */
                  media.stopLocalMedia();
                  break;
                }
                default: {
                  reject(new CaughtOTError(otError));
                }
              }
            }
            resolve();
          }),
        );

        return attempt;
      },
      (error) => {
        logger.error(prefix, error);
      },
    );

    this.publisherStats = new PublisherStats(publisher);

    logger.info(prefix, `published, ${attempts} attempts`);

    publisher.on('audioLevelUpdated', (event): void => {
      const { ownIdentityId: id } = this;
      if (typeof id !== 'undefined') {
        audioLevelChanges.next([id, event.audioLevel]);
      }
    });

    /**
     * Publisher could be re-created multiple times during the session:
     * - because of using troubleshooting
     * - media access changed
     * - ...
     * If self subscribes to captions we need to re-subscribe to own captions as well.
     */
    if (this.isSubscribingToCaptions) {
      try {
        await this.subscribeToSelfCaptions(true);
      } catch (error) {
        this.logger.error(
          '[SessionVonage/publish] - subscribe to own captions failed',
          error,
        );
      }
    }
  }

  private async endSession(): Promise<void> {
    if (this.publisher) {
      this.currentSession?.unpublish(this.publisher);
    }

    this.stopRepublishingOnSourceUpdate();
    /**
     * We need to remove all event handlers attached to the session
     * before disconnect to avoid any event handlers duplications
     * during restarting call
     * https://tokbox.com/developer/sdks/js/reference/OT.html#initSession
     */
    this.currentSession?.off();
    this.currentSession?.disconnect();
    this.currentSession = undefined;
    this.ownIdentityId = undefined;
    this.lastCPUPerformanceState = undefined;
    this.lastQualityScore = {};
    this.lastNetworkScores = undefined;
    this.subscribers.clear();
    /**
     * When session ended we need to stop and clean up the captions state.
     */
    if (this.isPublishingCaptions) {
      this.participantsWithCaptionsEnabledMap.clear();
      this.stopCaptions();
    }
  }

  /**
   * This function adds to all the collectors the latest values available from a call session.
   */
  async collectStats(): Promise<void> {
    for (const [_, scores] of Object.entries(this.lastQualityScore)) {
      if (scores.videoQualityScore) {
        statsCollectors.metricInboundVideoMos.add(scores.videoQualityScore);
      }

      if (scores.audioQualityScore) {
        statsCollectors.metricInboundAudioMos.add(scores.audioQualityScore);
      }
    }

    if (this.lastNetworkScores?.inbound) {
      const inbound = this.lastNetworkScores.inbound;
      statsCollectors.metricInboundNetworkMos.add(inbound);
    }

    if (this.lastNetworkScores?.inboundNetworkQuality) {
      const inbound = this.lastNetworkScores.inboundNetworkQuality;
      statsCollectors.metricInboundNetworkQualityRatio.add(inbound);
    }

    if (this.lastNetworkScores?.outbound) {
      const outbound = this.lastNetworkScores.outbound;
      statsCollectors.metricOutboundNetworkMos.add(outbound);
    }

    if (this.lastNetworkScores?.outboundNetworkQuality) {
      const outbound = this.lastNetworkScores.outboundNetworkQuality;
      statsCollectors.metricOutboundNetworkQualityRatio.add(outbound);
    }

    const isOneToOne = this.subscribers.size === 1;
    let totalInboundBitrate;
    let totalInboundVideoBitrate;
    let totalInboundAudioBitrate;
    for (const [_, subscriber] of this.subscribers) {
      const stat = await this.subscriberStats.get(subscriber)?.getStats();
      if (stat?.frameRate) {
        statsCollectors.metricInboundFrameRate.add(stat.frameRate);
      }

      // Due to the nature of changing resolution across multiple subscribers, this will most likely give a false positive result and spoil the overall statistics.
      if (isOneToOne && stat?.resolution) {
        statsCollectors.metricInboundResolution.add(stat.resolution);
        statsCollectors.metricInboundResolutionMostUsed.add(stat.resolution);
        statsCollectors.metricInboundResolutionChangeCount.add(stat.resolution);
      }

      totalInboundBitrate =
        stat?.bitrate !== undefined
          ? (totalInboundBitrate ?? 0) + stat.bitrate
          : totalInboundBitrate;
      totalInboundVideoBitrate =
        stat?.videoBitrate !== undefined
          ? (totalInboundVideoBitrate ?? 0) + stat.videoBitrate
          : totalInboundVideoBitrate;
      totalInboundAudioBitrate =
        stat?.audioBitrate !== undefined
          ? (totalInboundAudioBitrate ?? 0) + stat.audioBitrate
          : totalInboundAudioBitrate;
    }

    statsCollectors.metricInboundBitrate.add(totalInboundBitrate);
    statsCollectors.metricInboundVideoBitrate.add(totalInboundVideoBitrate);
    statsCollectors.metricInboundAudioBitrate.add(totalInboundAudioBitrate);

    const publisherStats = await this.publisherStats?.getStats();
    publisherStats?.map((stat) => {
      if (stat?.frameRate) {
        statsCollectors.metricOutboundFrameRate.add(stat.frameRate);
      }

      if (stat?.resolution) {
        statsCollectors.metricOutboundResolution.add(stat.resolution);
        statsCollectors.metricOutboundResolutionMostUsed.add(stat.resolution);
        statsCollectors.metricOutboundResolutionChangeCount.add(
          stat.resolution,
        );
      }

      statsCollectors.metricOutboundBitrate.add(stat.bitrate);
      statsCollectors.metricOutboundVideoBitrate.add(stat.videoBitrate);
      statsCollectors.metricOutboundAudioBitrate.add(stat.audioBitrate);
    });

    if (this.lastCPUPerformanceState) {
      statsCollectors.metricCpu.add(this.lastCPUPerformanceState);
    }
  }

  getMetricAggregators(): StatsCollectors {
    return statsCollectors;
  }

  getCPUPerformanceState(): CpuPerformanceState | undefined {
    return this.lastCPUPerformanceState;
  }

  /**
   * Returns actual instance of Publisher
   */
  private get publisher(): Publisher | undefined {
    return media.source as Publisher;
  }

  /**
   * When at least one person in the call wants to receive captions everyone should publish them.
   */
  private get isPublishingCaptions(): boolean {
    return this.participantsWithCaptionsEnabledMap.size > 0;
  }

  /**
   * If the self has enabled captions - we need to subscribe to them.
   */
  private get isSubscribingToCaptions(): boolean {
    return this.participantsWithCaptionsEnabledMap.has(
      this.ownIdentityId as string,
    );
  }

  /**
   * Returns true if the identifier is its own
   */
  private isOwnIdentityId(identityId?: string): boolean {
    if (!Boolean(identityId)) {
      throw new Error('Identity ID can not be empty');
    }

    return this.ownIdentityId === identityId;
  }

  /**
   * This method is called when any participant (own too) connects to the session
   */
  private onConnectionCreated({
    connection,
  }: {
    connection: Connection;
  }): void {
    const { identityId } = this.getConnectionMetadata(connection);

    this.transmitUsername(connection);

    /**
     * Request a new participant to start publishing captions for the self.
     */
    if (this.isSubscribingToCaptions) {
      const transmitToggleCaptionsLogPrefix = `[SessionVonage/onConnectionCreated] transmitToggleCaptions to ${identityId}`;
      this.transmitToggleCaptions('on', connection)
        .then(() => {
          return this.logger.info(`${transmitToggleCaptionsLogPrefix} success`);
        })
        .catch((error) => {
          this.logger.error(`${transmitToggleCaptionsLogPrefix} error`, error);
        });
    }

    this.logger.trace('[SessionVonage/onConnectionCreated]', {
      vonageConnectionId: connection?.connectionId,
      participantIdentityId: identityId,
    });

    if (!this.isOwnIdentityId(identityId)) {
      this.addParticipant({
        id: identityId,
        // TODO: add username inside stream.connection.data during token generation
        username: '',
        hasAudio: false,
        hasVideo: false,
        joinedAt: Date.now(),
        videoDisabledReason: null,
        videoDisableWarning: false,
        hasScreenAudio: false,
        networkQuality: undefined,
      });
    }
  }

  /**
   * The method is called when another participant disconnects from current session
   */
  private onConnectionDestroyed({
    connection,
  }: {
    connection: Connection;
  }): void {
    const { identityId } = this.getConnectionMetadata(connection);

    this.logger.trace('[SessionVonage/onConnectionDestroyed]', {
      vonageConnectionId: connection?.connectionId,
      participantIdentityId: identityId,
    });

    this.subscribers.delete(identityId);
    this.audioLevelChanges.next([identityId, undefined]);

    this.removeParticipant(identityId);

    /**
     * If participant left and there are others still in the call we need to remove him from the list of people who want to receive captions.
     */
    if (this.subscribers.size > 0) {
      this.handleParticipantCaptionsOff(identityId);
    }
  }

  /**
   * The method is called when another participant starts publishing a stream to a Session
   */
  private onStreamCreated({ stream }: { stream: Stream }): void {
    if (stream.videoType === 'screen') {
      void this.subscribeToScreenStream(stream);
    } else {
      void this.subscribeToStream(stream);
    }
  }

  private async subscribeToStream(stream: Stream): Promise<void> {
    const prefix = '[SessionVonage/subscribeToStream]';
    const { identityId } = this.getConnectionMetadata(stream.connection);
    const context = {
      vonageConnectionId: stream.connection?.connectionId,
      vonageStreamId: stream.streamId,
      participantIdentityId: identityId,
      videoType: stream.videoType,
    };

    const attempts = await retry(
      { maxAttempts: 3, delayBetweenAttempts: 500 },
      async (attempt) => {
        this.logger.debug(prefix, `subscribing, attempt ${attempt}`);

        const session = this.currentSession as OTSession;

        await new Promise<void>((resolve, reject) => {
          const subscriber = session.subscribe(
            stream,
            undefined,
            {
              insertDefaultUI: false,
              preferredResolution: undefined,
            },
            (error) => {
              if (error) {
                reject(new CaughtOTError(error));
              } else {
                resolve();
              }
            },
          ) as OTSubscriber;
          subscriber.on('disconnected', () => {
            this.logger.info('[SessionVonage/subscriberDisconnected]', context);
            this.updateParticipant(identityId, {
              connectionStatus: SessionConnectionStatus.Reconnecting,
            });
          });

          subscriber.on('connected', () => {
            this.logger.info('[SessionVonage/subscriberConnected]', context);
            this.updateParticipant(identityId, {
              connectionStatus: SessionConnectionStatus.Connected,
            });
          });

          subscriber.on('destroyed', ({ reason }): void => {
            this.logger.info('[SessionVonage/subscriberDestroyed]', context, {
              reason,
            });

            this.updateParticipant(identityId, {
              connectionStatus: SessionConnectionStatus.Disconnected,
            });

            this.subscribers.delete(identityId);
          });

          subscriber.on('videoElementCreated', (event) => {
            /**
             * The Vonage recommended way to watch for stream updates
             * @link https://tokbox.com/developer/guides/customize-ui/js/#subscriber-mediaStream
             */

            /**
             * @issue Audio is lost for a participant whose video never appeared on the screen.
             *        The participant's video is manually disabled (subscribeToVideo(false))
             *        for bandwidth and CPU optimization, but the audio is also affected.
             *        https://doxyme.atlassian.net/browse/CS-16085
             *
             * @solution After reproducing the issue multiple times with Rame and Javier from Vonage,
             *           we determined that explicitly calling subscribeToVideo(true) whenever a
             *           video element is created resolves the problem.
             *
             * @concerns This solution should not introduce any side effects, as the video should
             *           always be enabled initially. According to Vonage, the videoElementCreated
             *           event should not fire multiple times for the same participant, so it is
             *           safe to apply this fix universally.
             */
            subscriber.subscribeToVideo(true);

            const getParticipantStreamUpdates = (): Pick<
              UpdatableSessionParticipantData,
              'videoStream'
            > => {
              return {
                videoStream: (event.element as HTMLVideoElement)
                  .srcObject as MediaStream,
              };
            };

            event.element.addEventListener('play', () => {
              this.updateParticipant(identityId, getParticipantStreamUpdates());
            });
            this.updateParticipant(identityId, {
              ...getParticipantStreamUpdates(),
              videoElement: event.element,
              hasAudio: stream.hasAudio,
              hasVideo: stream.hasVideo,
            });

            void this.setAudioOutput(subscriber, media.selectedSpeakerId);
          });

          subscriber.on('audioLevelUpdated', (event): void => {
            this.audioLevelChanges.next([identityId, event.audioLevel]);
          });

          subscriber.on('videoDisabled', (e) => {
            const reason = e.reason as OTVideoDisableReason;

            this.logger.debug('[SessionVonage/subscriber/videoDisabled]', {
              context,
              reason,
            });

            this.updateParticipant(identityId, {
              videoDisabledReason: getParticipantVideoDisableReason(reason),
            });
          });

          subscriber.on('videoEnabled', ({ reason }) => {
            this.logger.debug('[SessionVonage/subscriber/videoEnabled]', {
              context,
              reason,
            });

            this.updateParticipant(identityId, {
              videoDisabledReason: null,
            });
          });

          /**
           * @see https://tokbox.com/developer/guides/audio-fallback/js/#enabling_audio_fallback
           */
          subscriber.on('videoDisableWarning', (event) => {
            this.logger.info(prefix, 'videoDisableWarning', {
              event,
            });

            this.updateParticipant(identityId, {
              videoDisableWarning: true,
            });
          });

          subscriber.on('videoDisableWarningLifted', (event) => {
            this.logger.info(prefix, 'videoDisableWarningLifted', {
              event,
            });

            this.updateParticipant(identityId, {
              videoDisableWarning: false,
            });
          });

          subscriber.on('qualityScoreChanged', (event) => {
            this.lastQualityScore[identityId] = event.qualityScore;
          });

          subscriber.subscribeToCaptions(true).catch((error) => {
            this.logger.error(`${prefix} subscribe to captions failed`, error);
          });

          subscriber.on('captionReceived', (event) => {
            if (this.isSubscribingToCaptions) {
              this.captionReceived(identityId, event.caption, event.isFinal);
            }
          });

          this.subscribers.set(identityId, subscriber);
          this.subscriberStats.set(subscriber, new SubscriberStats(subscriber));
        });

        return attempt;
      },
      (error) => {
        this.logger.error(prefix, error);
      },
    );

    this.logger.info(prefix, `subscribed, ${attempts} attempts`);
  }

  private async subscribeToScreenStream(stream: Stream): Promise<void> {
    const prefix = '[SessionVonage/subscribeToStream]';
    const { identityId } = this.getConnectionMetadata(stream.connection);
    const context = {
      vonageConnectionId: stream.connection?.connectionId,
      vonageStreamId: stream.streamId,
      participantIdentityId: identityId,
      videoType: stream.videoType,
    };

    const attempts = await retry(
      { maxAttempts: 3, delayBetweenAttempts: 500 },
      async (attempt) => {
        this.logger.debug(prefix, `subscribing, attempt ${attempt}`);

        const session = this.currentSession as OTSession;

        await new Promise<void>((resolve, reject) => {
          const subscriber = session.subscribe(
            stream,
            undefined,
            { insertDefaultUI: false },
            (error) => {
              if (error) {
                reject(new CaughtOTError(error));
              } else {
                resolve();
              }
            },
          ) as OTSubscriber;

          subscriber.on('destroyed', ({ reason }): void => {
            this.logger.info('[SessionVonage/subscriberDestroyed]', context, {
              reason,
            });

            this.updateParticipant(identityId, {
              screenElement: undefined,
            });
          });

          subscriber.on('videoElementCreated', (event) => {
            this.updateParticipant(identityId, {
              screenElement: event.element,
              hasScreenAudio: stream.hasAudio,
            });

            void this.setAudioOutput(subscriber, media.selectedSpeakerId);
          });

          this.screenSubscribers.set(identityId, subscriber);
        });

        return attempt;
      },
      (error) => {
        this.logger.error(prefix, error);
      },
    );

    this.logger.info(prefix, `subscribed, ${attempts} attempts`);
  }

  /**
   * The method is called when another participant stops publishing a stream to a Session
   */
  private onStreamDestroyed({
    stream,
    reason,
  }: {
    stream: Stream;
    reason: string;
  }): void {
    const { identityId } = this.getConnectionMetadata(stream.connection);
    const log = (event: string, data = {}): void => {
      this.logger.info(event, {
        vonageConnectionId: stream.connection?.connectionId,
        vonageStreamId: stream.streamId,
        participantIdentityId: identityId,
        videoType: stream.videoType,
        ...data,
      });
    };

    log('[SessionVonage/onStreamDestroyed]', { reason });

    if (stream.videoType === 'screen') {
      this.updateParticipant(identityId, {
        screenElement: undefined,
      });
    } else {
      this.updateParticipant(identityId, {
        hasVideo: false,
        hasAudio: false,
      });
    }

    this.audioLevelChanges.next([identityId, undefined]);
  }

  /**
   * The method is called when another participant sends a signal.
   */
  private onSignal(event: OTSignalEvent): void {
    const signalEvent = parseOTSignalEvent(event);
    if (!signalEvent) {
      return;
    }

    /**
     * Ignore signals if they come from the self connection.
     * ALLOWED_SELF_SIGNAL_EVENTS - list of signal events that are allowed to come from the self connection to be handled.
     */
    const ALLOWED_SELF_SIGNAL_EVENTS = [SignalingEventType.ToggleCaptions];
    if (
      signalEvent.from?.connectionId ===
        this.currentSession?.connection?.connectionId &&
      !ALLOWED_SELF_SIGNAL_EVENTS.includes(signalEvent.type)
    ) {
      return;
    }

    switch (signalEvent.type) {
      case SignalingEventType.CaptionsStatus: {
        const status = signalEvent.data;
        if (status === 'failed') {
          this.captionsFailed(this.isSubscribingToCaptions);

          if (this.isSubscribingToCaptions) {
            void this.subscribeToSelfCaptions(false);
          }
          this.participantsWithCaptionsEnabledMap.clear();
        }

        break;
      }
      case SignalingEventType.ToggleCaptions: {
        const { identityId } = this.getConnectionMetadata(signalEvent.from);

        /**
         * If someone requests captions, we want to set them in the `participantsWithCaptionsEnabledMap`
         * also, if it's the 1st participant who enabled, we want to enable captions for the session via API
         */
        if (signalEvent.data === 'on') {
          this.participantsWithCaptionsEnabledMap.set(identityId, true);
          /**
           * If you toggle your own captions size expected already be set to 1 in toggleCaptions (to avoid race conditions)
           */
          if (this.participantsWithCaptionsEnabledMap.size === 1) {
            // Enabling captions via session API
            this.startCaptions();
          }
          /**
           * If someone switches off captions, we want to remove them from `participantsWithCaptionsEnabledMap`
           * and if there is no one with captions enabled, disable the captions for the session via API
           */
        } else if (signalEvent.data === 'off') {
          this.handleParticipantCaptionsOff(identityId);
        }

        break;
      }
      case SignalingEventType.TransmitUsername: {
        const { identityId } = this.getConnectionMetadata(signalEvent.from);
        this.updateParticipant(identityId, {
          username: signalEvent.data,
        });
        break;
      }
      case SignalingEventType.TransmitNetworkQuality: {
        const { identityId } = this.getConnectionMetadata(signalEvent.from);
        this.updateParticipant(identityId, {
          networkQuality: signalEvent.data as NetworkQuality,
        });
        break;
      }
      default: {
        // ignore
        break;
      }
    }
  }

  /**
   * Fired when one of the properties of a stream change, for example when audio or video is toggled.
   *
   * @param event OT:Event
   */
  private onStreamChanged({
    stream,
    changedProperty,
    newValue,
  }: {
    stream: Stream;
    changedProperty:
      | 'hasVideo'
      | 'hasAudio'
      | 'hasCaptions'
      | 'videoDimensions';
    newValue: boolean | Dimensions;
  }): void {
    const { identityId } = this.getConnectionMetadata(stream.connection);
    if (stream.videoType === 'screen') {
      return;
    }

    if (changedProperty === 'hasVideo' || changedProperty === 'hasAudio') {
      this.updateParticipant(identityId, {
        [changedProperty]: newValue,
      });
    }

    if (changedProperty === 'hasAudio' && newValue === false) {
      this.audioLevelChanges.next([identityId, undefined]);
    }
  }

  /**
   * Returns individual data that was added when generating the token
   *
   * Details: https://tokbox.com/developer/guides/create-token/
   */
  private getConnectionMetadata(connection: Connection): ConnectionMetadata {
    try {
      return JSON.parse(connection.data) as ConnectionMetadata;
    } catch (error) {
      this.logger.error(
        '[SessionVonage/getConnectionMetadata]: invalid metadata',
        error,
      );

      throw error;
    }
  }

  /**
   * Transmits the username to the other participant when they first joined the call.
   */
  private transmitUsername(connection?: Connection): void {
    if (this.username === undefined) {
      return;
    }

    this.currentSession?.signal(
      {
        type: SignalingEventType.TransmitUsername,
        data: this.username,
        to: connection,
      },
      (error) => {
        if (error) {
          const parsedError = new CaughtOTError(error);
          this.logger.error('[SessionVonage/transmitUsername]', parsedError);
        }
      },
    );
  }

  /**
   * Transmits the network quality value to all participants in session
   */
  updateNetworkQualityForAllParticipants(scores: ProcessedNetworkScores): void {
    const previous = this.lastNetworkScores;
    this.lastNetworkScores = scores;

    if (
      scores.outboundNetworkQuality === undefined ||
      previous?.outboundNetworkQuality === scores.outboundNetworkQuality
    ) {
      return;
    }

    this.currentSession?.signal(
      {
        type: SignalingEventType.TransmitNetworkQuality,
        data: scores.outboundNetworkQuality,
      },
      (error) => {
        if (error) {
          const parsedError = new CaughtOTError(error);
          this.logger.error(
            '[SessionVonage/transmitNetworkQuality]',
            parsedError,
          );
        }
      },
    );
  }

  async startScreenShare(): Promise<void> {
    const prefix = '[SessionVonage/startScreenShare]';

    const opentok = await this.opentokClient!;

    return new Promise((resolve, reject) => {
      opentok.checkScreenSharingCapability(async (response): Promise<void> => {
        if (!response.supported || response.extensionRegistered === false) {
          reject(new Error("Browser doesn't support screen sharing"));
        } else if (response.extensionInstalled === false) {
          reject(
            new Error('Browser needs to install screen sharing extension'),
          );
        } else {
          try {
            await media.initScreenSharePublisher();
          } catch (error) {
            this.logger.error(
              prefix,
              'Error initializing screen share publisher:',
              error,
            );
            // Handle the error here (e.g., display an error message to the user)
            reject(error);
            return;
          }
          const screenPublisher: Publisher =
            media.screenShareSource as Publisher;

          const session = this.currentSession as OTSession;

          session.publish(screenPublisher, (error) => {
            if (error) {
              this.logger.error(prefix, error);
              reject(error);
            } else {
              this.logger.info(
                prefix,
                'Screen share publisher successfully published',
              );
            }
          });

          resolve();
        }
      });
    });
  }

  async stopScreenShare(): Promise<void> {
    const screenPublisher: Publisher | undefined =
      media.screenShareSource as Publisher;

    if (Boolean(screenPublisher)) {
      screenPublisher.destroy();
      this.logger.info(
        '[SessionVonage/stopScreenShare]',
        'Screen share publisher destroyed successfully',
      );
    }
  }

  /**
   * Initiated by self to enable/disable captions.
   */
  async toggleCaptions(isEnabled: boolean): Promise<void> {
    const prefix = `[SessionVonage/toggleCaptions] ${isEnabled ? 'on' : 'off'}`;
    this.logger.info(`${prefix} start`);

    try {
      /**
       * Subscribe to self captions.
       */
      await this.subscribeToSelfCaptions(isEnabled);

      /**
       * Signal to others to start/stop publishing captions.
       */
      await this.transmitToggleCaptions(isEnabled ? 'on' : 'off');
    } catch (error) {
      this.logger.error(`${prefix} error`, error);
      if (isEnabled) {
        /**
         * Notify the app that captions failed.
         */
        this.captionsFailed(true);
        /**
         * When captions failed we need to make sure everyone will stop publishing them.
         */
        void this.transmitToggleCaptions('off');
      }

      throw error;
    }

    this.logger.info(`${prefix} success`);
  }

  /**
   * Transmits a signal to other participants to toggle captions on or off.
   *
   * @param connection - (Optional) The specific connection to send the signal to. If not provided, the signal is sent to all participants.
   *
   * This method checks if the self participant has enabled captions and sends a signal with the appropriate data ('on' or 'off')
   * to either the specified connection or all participants in the current session. If an error occurs during signaling,
   * it logs the error using the session's logger.
   */
  private async transmitToggleCaptions(
    data: 'on' | 'off',
    connection?: Connection,
  ): Promise<void> {
    await new Promise((resolve, reject) => {
      this.currentSession?.signal(
        {
          type: SignalingEventType.ToggleCaptions,
          data,
          ...(connection && { to: connection }),
        },
        (error) => {
          if (error) {
            const parsedError = new CaughtOTError(error);
            this.logger.error(
              '[SessionVonage/transmitToggleCaptions]',
              parsedError,
            );
            reject(parsedError);
          } else {
            resolve(void 0);
          }
        },
      );
    });
  }

  private async subscribeToSelfCaptions(value: boolean): Promise<void> {
    if (this.selfCaptionsSubscriber) {
      try {
        this.currentSession?.unsubscribe(this.selfCaptionsSubscriber);
      } catch (error) {
        this.logger.error(
          '[SessionVonage/subscribeToSelfCaptions] - unsubscribe from the old one failed',
          error,
        );
      }
    }
    /**
     * No need to create a new subscriber if we do not need captions.
     */
    if (!value) {
      this.selfCaptionsSubscriber = undefined;
      return;
    }
    /**
     * To render the speaker's own captions, create a hidden subscriber (to the local publisher's stream) to listen for the caption events.
     * @link https://developer.vonage.com/en/video/guides/live-caption?lang=javascript#:~:text=is%20my%20sentence%22-,Receiving%20your%20own%20live%20captions,-The%20Vonage%20web
     */

    this.selfCaptionsSubscriber = await new Promise<Subscriber | undefined>(
      (resolve, reject) => {
        if (!this.publisher?.stream) {
          this.logger.warn(
            '[SessionVonage/subscribeToSelfCaptions] - no publisher stream',
          );
          resolve(undefined);
          return;
        }
        const selfCaptionsSubscriber = this.currentSession?.subscribe(
          this.publisher.stream,
          document.createElement('div'),
          {
            audioVolume: 0,
          },
          (error) => {
            if (error) {
              const parsedError = new CaughtOTError(error);
              this.logger.error(
                '[SessionVonage/subscribeToSelfCaptions]',
                parsedError,
              );
              reject(error);
            } else {
              resolve(selfCaptionsSubscriber as Subscriber);
            }
          },
        );
      },
    );

    this.selfCaptionsSubscriber?.on('captionReceived', (event) => {
      this.captionReceived(
        this.ownIdentityId as string,
        event.caption,
        event.isFinal,
      );
    });

    try {
      await this.selfCaptionsSubscriber?.subscribeToCaptions(value);
    } catch (error) {
      this.logger.error(
        '[SessionVonage/subscribeToSelfCaptions] - subscribe to captions failed',
        error,
      );
      throw error;
    }
  }

  private handleParticipantCaptionsOff(identityId: string): void {
    if (!this.participantsWithCaptionsEnabledMap.has(identityId)) {
      return;
    }
    this.participantsWithCaptionsEnabledMap.delete(identityId);
    if (this.participantsWithCaptionsEnabledMap.size === 0) {
      // Disabling captions via session API
      this.stopCaptions();
    }
  }

  get sessionId(): string | undefined {
    return this._sessionData?.sessionId;
  }

  get joinTokens(): SessionData['joinTokens'] | undefined {
    return this._sessionData?.joinTokens;
  }

  get sessionOwnershipToken(): string | undefined {
    return this._sessionData?.ownershipToken;
  }

  get identityId(): string | undefined {
    return this._sessionData?.identityId;
  }

  get participantsMap(): Record<string, SessionParticipant> {
    return Object.fromEntries(this.participants);
  }

  /**
   * Returns the identityIds of the all participants in the session, including self.
   */
  get allIdentityIds(): string[] {
    return this.identityId
      ? [this.identityId, ...Object.keys(this.participantsMap)]
      : [];
  }

  get connectionStatus(): SessionConnectionStatus | undefined {
    return this._connectionStatus;
  }

  get sessionType(): SessionType | undefined {
    return this._sessionOptions?.sessionType;
  }

  /**
   * Create a new webRTC session from the backend using V2 secure session API
   */
  async createSessionV2(
    createParameters: SessionStartParams,
  ): Promise<SessionResponseData | ResponseWithError> {
    const { store } = await import('../../stores/redux');
    const response = await store.dispatch(
      sessionApiService.endpoints.createSessionV2.initiate(
        {
          identityIds: createParameters.identityIds,
          credentials: createParameters.credentials,
          sessionType:
            WebRtcSessionTypeMap[
              createParameters.sessionType ?? SessionType.Routed
            ],
        },
        { forceRefetch: true },
      ),
    );
    return response as SessionResponseData | ResponseWithError;
  }

  /**
   * Generates join tokens for new participants and adds them to the session data.
   * To be used by provider to add more participants to the session.
   */
  async addParticipants({
    identityIds,
    credentials,
  }: SessionAddParticipantsParams): Promise<void> {
    const prefix = '[addParticipants]';

    const { store } = await import('../../stores/redux');

    const response = (await store.dispatch(
      sessionApiService.endpoints.joinSessionV2.initiate(
        {
          ownershipToken: this._sessionData?.ownershipToken as string,
          identityIds,
          credentials,
        },
        { forceRefetch: true },
      ),
    )) as { data: WebRtcSessionV2 } | ResponseWithError;

    try {
      if (ensureSuccessfulSessionApiResponse(response)) {
        this.logger.info(prefix, 'Participants added', {
          sessionId: response.data.sessionId,
          identityIds,
        });

        this._sessionData!.joinTokens = [
          ...this._sessionData!.joinTokens!,
          ...response.data.joinTokens,
        ];
      }
    } catch (error) {
      if (error instanceof Error) {
        this.logger.error(prefix, error.message);
      }
      throw error;
    }
  }

  /**
   * Ensures there's only ever one setup running session at any given time
   */
  private async initSessionGuard(start: () => Promise<void>): Promise<void> {
    this.sessionStartGuard =
      this.sessionStartGuard ??
      (async (): Promise<void> => {
        try {
          await start();
        } catch (error) {
          this.logger.error(`[initSessionStart/start]`, error);
          throw error;
        } finally {
          this.sessionStartGuard = undefined;
        }
      })();
    return this.sessionStartGuard;
  }

  /**
   * Fetches a sessionId from the api. TODO: can be renamed.
   */
  async start(createParameters: SessionStartParams): Promise<void> {
    const prefix = `[createSession]`;
    return this.initSessionGuard(async () => {
      const { store } = await import('../../stores/redux');
      sessionSetupTimer.step(SessionSetupStep.ReduxStoreImported);

      const response = await this.createSessionV2(createParameters);

      try {
        if (ensureSuccessfulSessionApiResponse(response)) {
          const { data } = response;
          this.logger.info(prefix, `Created session, id: ${data.sessionId}`);
          sessionSetupTimer.step(SessionSetupStep.SessionCreated);

          this._sessionData = {
            sessionId: data.sessionId,
            ownershipToken: data.ownershipToken,
            joinTokens: data.joinTokens,
            sessionToken: data.joinTokens.find(
              (token) => token.identityId === createParameters.identityId,
            )?.token as string,
            identityId: createParameters.identityId,
          };

          this._sessionOptions = {
            sessionType: createParameters.sessionType,
            username: getUsername(store.getState()),
          };
        }
      } catch (error) {
        if (error instanceof Error) {
          this.logger.error(prefix, error.message);
        }
        throw error;
      }
    });
  }

  async join(joinParameters: SessionJoinParams): Promise<void> {
    return this.initSessionGuard(async () => {
      const { store } = await import('../../stores/redux');
      sessionSetupTimer.step(SessionSetupStep.ReduxStoreImported);

      this._sessionData = {
        identityId: joinParameters.identityId,
        sessionId: joinParameters.sessionId,
        sessionToken: joinParameters.joinToken,
      };
      this._sessionOptions = {
        username: getUsername(store.getState()),
      };
    });
  }

  /**
   * Generates join tokens for new participants and adds them to the session data.
   * To be called by provider when already on the session but wants to add new participants.
   */
  async resume({
    identityId,
    ownershipToken,
    identityIds,
    credentials,
  }: SessionResumeParams): Promise<void> {
    const prefix = '[resume]';

    return this.initSessionGuard(async () => {
      const { store } = await import('../../stores/redux');
      sessionSetupTimer.step(SessionSetupStep.ReduxStoreImported);

      const response = (await store.dispatch(
        sessionApiService.endpoints.joinSessionV2.initiate(
          {
            ownershipToken,
            identityIds,
            credentials,
          },
          { forceRefetch: true },
        ),
      )) as { data: WebRtcSessionV2 } | ResponseWithError;

      try {
        if (ensureSuccessfulSessionApiResponse(response)) {
          this.logger.info(prefix, 'Resume tokens generated', {
            sessionId: response.data.sessionId,
            ownershipToken,
            identityIds,
          });

          this._sessionData = {
            sessionId: response.data.sessionId,
            ownershipToken,
            joinTokens: response.data.joinTokens,
            sessionToken: response.data.joinTokens.find(
              (token) => token.identityId === identityId,
            )?.token as string,
            identityId,
          };

          this._sessionOptions = {
            username: getUsername(store.getState()),
          };
          sessionSetupTimer.step(SessionSetupStep.SessionJoined);
        }
      } catch (error) {
        if (error instanceof Error) {
          this.logger.error(prefix, error.message);
        }
        throw error;
      }
    });
  }

  async connect(): Promise<void> {
    await this.startSession();
    this.metricsMonitor?.reset();
    this.metricsMonitor?.start();
    sessionSetupTimer.step(SessionSetupStep.SessionStarted);
  }

  /**
   * Used to connect to the same session again after disconnect.
   */
  async reconnect(): Promise<void> {
    try {
      await this.startSession();
    } catch (error) {
      if (error instanceof Error) {
        this.logger.error('[reconnect]', error.message);
      }
      throw error;
    }
  }

  /**
   * Disconnect from the session to further reconnect.
   */
  async disconnect(): Promise<void> {
    await this.endSession();

    this._connectionStatus = SessionConnectionStatus.Disconnected;
    this.participants.clear();
    this.fire([
      {
        type: SessionEventType.SessionDisconnected,
      },
    ]);
  }

  async end(): Promise<void> {
    this.metricsMonitor?.stop();
    this.participantsPermanentData.clear();

    await this.endSession();
    this._connectionStatus = SessionConnectionStatus.Disconnected;
    this.participants.clear();
    this.fire([
      {
        type: SessionEventType.SessionDisconnected,
      },
    ]);

    this._sessionData = undefined;
    this._sessionOptions = undefined;
  }

  getMetrics(): StatsToLog | undefined {
    return this.metricsMonitor?.getMetrics();
  }

  private updateConnectionStatus(
    connectionStatus: SessionConnectionStatus,
  ): void {
    if (this._connectionStatus !== connectionStatus) {
      this._connectionStatus = connectionStatus;
      this.fire([
        {
          type: SessionEventType.ConnectionStatusChanged,
          data: { connectionStatus },
        },
      ]);
    }
  }

  private addParticipant(participant: SessionParticipant): void {
    const tempData = this.participantsTempData.get(participant.id);
    const permanentData = this.participantsPermanentData.get(participant.id);
    if (!permanentData) {
      this.participantsPermanentData.set(participant.id, {
        joinedAt: participant.joinedAt,
      });
    }
    const username = participant.username || (tempData?.username ?? '');
    const newParticipant = {
      ...tempData,
      ...participant,
      username,
      ...permanentData,
    };

    if (participant.id.startsWith('Provider')) {
      // Ensure that the provider is always the first participant in the map:
      const newEntry = [participant.id, newParticipant] as const;
      this.participants = new Map([newEntry, ...this.participants.entries()]);
    } else {
      this.participants.set(participant.id, newParticipant);
    }

    this.fire([
      {
        type: SessionEventType.ParticipantJoined,
        data: { participantId: participant.id },
      },
    ]);
  }

  private async startCaptions(): Promise<void> {
    if (this.identityId?.startsWith('Provider')) {
      try {
        const { store } = await import('../../stores/redux');
        const response = await store.dispatch(
          sessionApiService.endpoints.startLiveCaptions.initiate({
            sessionId: this.sessionId as string,
          }),
        );
        this.captionsId = response.data?.captionsId;
        this.logger.info('[startCaptions]', 'Captions started successfully', {
          sessionId: this.sessionId,
        });
      } catch (error) {
        if (error instanceof Error) {
          this.logger.error('[startCaptions]', error.message);
        }
        throw error;
      }
    }
  }

  private async stopCaptions(): Promise<void> {
    if (this.captionsId && this.identityId?.startsWith('Provider')) {
      try {
        const { store } = await import('../../stores/redux');
        await store.dispatch(
          sessionApiService.endpoints.stopLiveCaptions.initiate({
            captionsId: this.captionsId,
          }),
        );

        this.captionsId = undefined;

        this.logger.info('[stopCaptions]', 'Captions stopped successfully', {
          sessionId: this.sessionId,
        });
      } catch (error) {
        if (error instanceof Error) {
          this.logger.error('[stopCaptions]', error.message);
        }
        throw error;
      }
    }
  }

  private captionReceived(
    identityId: string,
    caption: string,
    isFinal: boolean,
  ): void {
    this.fire([
      {
        type: SessionEventType.CaptionReceived,
        data: { identityId, caption, isFinal },
      },
    ]);
  }

  private captionsFailed(isSelfCaptionsEnabled: boolean): void {
    if (isSelfCaptionsEnabled) {
      this.fire([
        {
          type: SessionEventType.CaptionFailed,
        },
      ]);
    }

    if (this.captionsId && this.identityId?.startsWith('Provider')) {
      this.stopCaptions();
    }
  }

  private updateParticipant(
    participantId: string,
    participant: Partial<UpdatableSessionParticipantData> = {},
  ): void {
    const current = this.participants.get(participantId);

    if (!current) {
      this.participantsTempData.set(participantId, participant);
      return;
    }

    this.participants.set(participantId, { ...current, ...participant });

    if (participant.videoDisabledReason) {
      this.fire([
        {
          type: SessionEventType.ParticipantVideoDisabled,
          data: {
            participantId,
            videoDisabledReason: participant.videoDisabledReason,
          },
        },
      ]);
    }

    if (participant.videoDisableWarning) {
      this.fire([
        {
          type: SessionEventType.ParticipantVideoDisableWarning,
          data: {
            participantId,
          },
        },
      ]);
    }

    this.fire([
      { type: SessionEventType.ParticipantUpdated, data: { participantId } },
    ]);
  }

  private removeParticipant(participantId: string): void {
    this.participants.delete(participantId);

    this.fire([
      { type: SessionEventType.ParticipantLeft, data: { participantId } },
    ]);
  }

  async hold(): Promise<void> {
    this.metricsMonitor?.stop();

    await this.endSession();

    this._connectionStatus = SessionConnectionStatus.Disconnected;
    this.fire([
      {
        type: SessionEventType.SessionDisconnected,
      },
    ]);
  }

  clearParticipants(): void {
    this.participants.forEach((participant) =>
      this.removeParticipant(participant.id),
    );
  }
}

export const session = new Session(
  getFrontendConfigLoader().get('SESSION_VONAGE_API_KEY') as string,
);
